import {
  getCurrentUser,
  deleteCurrentUser,
  setCurrentUser,
} from "@/helpers/helper";
const localUser = getCurrentUser();
import Api from "@/services/api";
import router from "@/router/index";
import store from "@/store";
import {jwtDecode} from "jwt-decode";
import {
  LOGIN,
  LOGOUT,
  REFRESHTOKEN,
  SWITCH_ACCOOUNT,
  LOAD_ORGANIZATION,
  LOAD_PROJECT,
  SEND_PASSWORD_RESET_LINK,
  VERIFY_RESET_PASSWORD_LINK,
  RESET_PASSWORD,
  ADD_ALERT,
  UPDATE_INTERACTIONS,
  GET_USER_TAGS,
  INIT_PROJECT_CONTEXT,
} from "@/store/_actiontypes";
import {
  LOGIN_SUCCESS,
  LOGIN_FAILURE,
  LOGOUT_USER,
  SWITCH_SUCCESS,
  SET_ORGANIZATION,
  SET_ACCESS_TAGS,
  SET_PROJECT_ROLE,
  SET_ENTRY_ACCESS,
  SET_RESET_PASSWORD_USER_DETAILS,
} from "@/store/_mutationtypes";
import { INTERACTION_POINT } from "@/global/_constants";
import getEnv from "@/config/env";

const state = {
  user: localUser ? localUser : null,
  currencies: [],
  organization: {},
  login_error: null,
  signup_error: null,
  resetPasswordUserDetails: {},
  account: {},
  userTags: [],
  projectRole: null,
};

const actions = {
  async [LOGIN]({ commit }, { email, password, recaptcha_token, clientIP }) {
    let response;
    try {
      response = await Api.post(
        "oauth2/token?grant_type=password",
        {
          email: email,
          password: password,
        },
        {
          headers: {
            "X-Captcha-Token": recaptcha_token,
            "X-Client-IP": clientIP,
          },
        }
      );
    } catch (error) {
      commit(LOGIN_FAILURE);
      throw error;
    }

    // Validates the user against the configured account/project list.
    let activeEntry = null;
    try {
      activeEntry = await store.dispatch(`project/${INIT_PROJECT_CONTEXT}`, {
        access_token: response.data?.access_token,
        force: true,
      });
    } catch (error) {
      console.error("Error initializing project context:", error);
    }

    if (!activeEntry) {
      commit(LOGIN_FAILURE);
      throw new Error("User has no access to any configured account project");
    }

    // Required by the interaction below.
    try {
      await store.dispatch(`account/${LOAD_ORGANIZATION}`);
    } catch (error) {
      console.error("Error loading organization after login:", error);
    }

    // Log interaction
    store.dispatch(`interactions/${UPDATE_INTERACTIONS}`, {
      interaction_point: INTERACTION_POINT.LOGIN,
      text_in_interaction_point: "ログイン",
      interaction_value: email,
    });

    return response;
  },

  // Exchanges the given token for one scoped to account_id.
  [SWITCH_ACCOOUNT]({ commit }, { account_id, access_token = null, apply = true }) {
    return Api.put(
      "users/switch/accounts",
      { account_id },
      {
        headers: access_token ? { Authorization: `Bearer ${access_token}` } : {},
        skipAuthRedirect: true,
      }
    ).then((response) => {
      if (apply && response.data?.access_token) {
        commit(SWITCH_SUCCESS, response.data);
      }
      return response;
    });
  },
  [LOAD_ORGANIZATION]({ commit }) {
    return new Promise((resolve, reject) => {
      Api.get("users/me")
        .then((response) => {
          commit(SET_ORGANIZATION, response.data);
          resolve(response);
        })
        .catch((error) => {
          commit(SET_ORGANIZATION, {});
          reject(error);
        });
    });
  },
  [LOAD_PROJECT]({ commit }, { project_id, access_token = null }) {
    return new Promise((resolve, reject) => {
      // Load user tags for the project, then commit its role and resolve.
      const finishWithRole = async (role, response) => {
        const token = access_token || getCurrentUser()?.access_token;
        const user_id = token ? jwtDecode(token).sub : '';

        // Prevent GET_USER_TAGS from breaking navigation
        try {
          await store.dispatch(`account/${GET_USER_TAGS}`, {
            project_id: project_id,
            user_id: user_id,
            account_id: store.getters["project/activeAccountId"],
          });
        } catch (err) {
          console.error("Failed to load user tags:", err);
        }
        commit(SET_PROJECT_ROLE, role);
        store.commit(`project/${SET_ENTRY_ACCESS}`, { project_id, role });
        resolve(response);
      };

      // Reuse the role already fetched while validating.
      const cachedRole = store.getters["project/roleByProjectId"](project_id);
      if (cachedRole) {
        finishWithRole(cachedRole, {
          data: { project_role: cachedRole },
          status: 200,
        });
        return;
      }

      Api.get(`projects/${project_id}`, {
        headers: access_token
          ? { Authorization: `Bearer ${access_token}` }
          : {},
      })
        .then((response) => {
          finishWithRole(response?.data?.project_role, response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  [GET_USER_TAGS]({ commit }, { project_id, user_id, account_id }) {
    return new Promise((resolve, reject) => {
      Api.get(`projects/${project_id}/users/${user_id}/tags?account_id=${account_id}`)
        .then((response) => {
          commit(SET_ACCESS_TAGS, response.data.tags || []);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  [SEND_PASSWORD_RESET_LINK](
    { dispatch },
    { email, reset_page_route, recaptcha_token, clientIP }
  ) {
    return new Promise((resolve, reject) => {
      Api.post(
        `users/passwords/${email}/forgot`,
        {
          reset_page_route: reset_page_route,
          recaptcha_token: recaptcha_token,
        },
        {
          headers: {
            "X-Captcha-Token": recaptcha_token,
            "X-Client-IP": clientIP,
          },
        }
      )
        .then(
          (response) => {
            dispatch(
              `alert/${ADD_ALERT}`,
              { message: response.data.message, color: "success" },
              { root: true }
            );
            resolve(response);
          },
          (error) => {
            reject(error);
          }
        )
        .catch((error) => {
          console.error("Error sending password reset link:", error);
        });
    });
  },
  [VERIFY_RESET_PASSWORD_LINK]({ commit }, { token }) {
    commit(SET_RESET_PASSWORD_USER_DETAILS, {});
    return new Promise((resolve, reject) => {
      Api.get(`users/passwords/${token}/reset`).then(
        response => {
          // Invalidate any existing local session when the reset link is opened
          commit(LOGOUT_USER);
          if (response.data) {
            commit(SET_RESET_PASSWORD_USER_DETAILS, response.data);
          }
          resolve(response);
        },
        error => {
          reject(error);
        },
      );
    });
  },
  [RESET_PASSWORD](
    { commit, dispatch },
    { token, password, recaptcha_token, clientIP },
  ) {
    return new Promise((resolve, reject) => {
      Api.post(
        `users/passwords/${token}/reset`,
        {
          password: password,
        },
        {
          headers: {
            "X-Captcha-Token": recaptcha_token,
            "X-Client-IP": clientIP,
          },
        },
      )
        .then(
          response => {
            dispatch(
              `alert/${ADD_ALERT}`,
              { message: response.data.message, color: "success" },
              { root: true },
            );
            // Always clear the current user session after a successful password reset
            commit(LOGOUT_USER);
            resolve(response);
            router.push({ name: "LoginPage" });
          },
          error => {
            reject(error);
          },
        )
        .catch(error => {
          console.error("Error resetting password:", error);
          reject(error);
        });
    });
  },
  async [REFRESHTOKEN](
    { commit, state },
    { refresh_token }
  ) {
    const account_id = store.getters["project/activeAccountId"];
    let payload = account_id ? { refresh_token, account_id } : { refresh_token };
    try {
      const response = await Api.post(
        "oauth2/token?grant_type=refresh_token",
        payload
      );

      const user = {
        access_token: response.data.access_token,
        refresh_token: response.data.refresh_token,
        expires_at: response.data.expires_at,
        token_type: "Bearer",
      };
      commit(LOGIN_SUCCESS, { user });
      try {
        await store.dispatch(`account/${LOAD_PROJECT}`, {
          project_id: store.getters["project/activeProjectId"],
        });
        await store.dispatch(`account/${LOAD_ORGANIZATION}`);
      } catch (err) {
        console.error("Failed to reload project/organization after refresh:", err);
      }

      store.dispatch(`interactions/${UPDATE_INTERACTIONS}`, {
        interaction_point: INTERACTION_POINT.REFRESHTOKEN,
        text_in_interaction_point: `リフレッシュトークン`,
        interaction_value:
          state.organization?.id || getEnv("VUE_APP_DEFAULT_USER_ID"),
      });
      return response;
    } catch (error) {
      commit(LOGIN_FAILURE);
      throw error;
    }
  },

  async [LOGOUT]({ commit }, { refresh_token }) {
    try {
      await Api.post("oauth2/revoke", { refresh_token });

      commit(LOGOUT_USER);
      await router.push("/login");
    } catch (error) {
      console.error("Error during logout:", error);
    }
  }
};

const mutations = {
  [LOGIN_SUCCESS](state, { user }) {
    state.user = user;
    // login successful if there's a jwt token in the response
    if (user.access_token) {
      // store user details and jwt token in local storage
      setCurrentUser(user);
    }
  },
  [LOGIN_FAILURE](state) {
    state.user = null;
    // remove user from local storage
    deleteCurrentUser();
  },
  [SWITCH_SUCCESS](state, user) {
    if (user.access_token) {
      setCurrentUser(user);
    }
    state.user = user;
  },
  [SET_ORGANIZATION](state, organization) {
    state.organization = organization;
  },
  [SET_RESET_PASSWORD_USER_DETAILS](state, resetPasswordUserDetails) {
    state.resetPasswordUserDetails = resetPasswordUserDetails;
  },
  [LOGOUT_USER](state) {
    deleteCurrentUser();
    // state.user = null;
    // Reset the state by replacing its properties with the default state
    Object.assign(state, {
      user: null,
      currencies: [],
      organization: {},
      login_error: null,
      signup_error: null,
      resetPasswordUserDetails: {},
      account: {},
    });
    //TODO - useless
    // this.reset();
  },
  [SET_ACCESS_TAGS](state, access_tags) {
    state.userTags = access_tags;
  },
  [SET_PROJECT_ROLE](state, role) {
    state.projectRole = role;
  },
};

const getters = {
  currentUser: (state) => state.user,
  userProfile: (state) => {
    return state.organization;
  },
  userTags: (state) => state.userTags,
  isAdmin: (state) => state.projectRole === 'admin',
};

export const account = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
